import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export type SignatureChoice = { mode: 'typed' } | { mode: 'drawn'; image: string };
export type SignatureStyle = 'draw_or_type' | 'draw' | 'type' | 'none';

interface SignaturePadProps {
  style: Exclude<SignatureStyle, 'none'>;
  signerName: string;
  /** null tant que le signataire n'a rien tracé (mode tracé) : la signature est alors impossible */
  onChange: (choice: SignatureChoice | null) => void;
}

const CANVAS_WIDTH = 720;
const CANVAS_HEIGHT = 240;
const INK = '#0a0a5a';
const LINE_WIDTH = 4;
// En plein écran la zone prend le rectangle le plus grand possible, entre ces deux formats (largeur / hauteur)
const MIN_RATIO = 4 / 3;
const MAX_RATIO = CANVAS_WIDTH / CANVAS_HEIGHT;

type Stroke = Array<[number, number]>;

/**
 * Zone de signature : tracer au doigt, au stylet ou à la souris, ou taper son nom.
 * Le tracé est envoyé en PNG transparent ; le service le recadre et le place dans le cachet.
 */
function SignaturePad({ style, signerName, onChange }: SignaturePadProps) {
  const [mode, setMode] = useState<'drawn' | 'typed'>(style === 'type' ? 'typed' : 'drawn');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  // Le tracé est gardé en points (largeur fixe de 720) pour être redessiné quand la zone change de hauteur
  const strokes = useRef<Stroke[]>([]);
  const [hasInk, setHasInk] = useState(false);
  // Plein écran : même zone (même tracé), agrandie au maximum de l'écran en gardant son format rectangulaire
  const [expanded, setExpanded] = useState(false);
  const [viewport, setViewport] = useState({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    if (!expanded) return;
    const update = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    update();
    // Tourner le téléphone redimensionne la zone sans perdre le tracé
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      document.body.style.overflow = previousOverflow;
    };
  }, [expanded]);

  // Rectangle disponible à l'écran (barre d'outils et indication déduites) et format retenu pour la zone
  const TOOLBAR = 64;
  const availableWidth = Math.max(200, viewport.width - 24);
  const availableHeight = Math.max(120, viewport.height - TOOLBAR - 56);
  const expandedRatio = expanded
    ? Math.min(MAX_RATIO, Math.max(MIN_RATIO, availableWidth / availableHeight))
    : MAX_RATIO;
  const canvasHeight = Math.round(CANVAS_WIDTH / expandedRatio);
  const expandedWidth = Math.floor(Math.min(availableWidth, availableHeight * expandedRatio));
  const portrait = viewport.height > viewport.width;

  const point = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) * canvas.width) / rect.width,
      y: ((event.clientY - rect.top) * canvas.height) / rect.height,
    };
  };

  // La hauteur de la zone change (plein écran, rotation) : redimensionner efface le canvas, le tracé est redessiné.
  // Au retour au format normal, un tracé plus haut que la zone est réduit pour tenir entièrement.
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || canvas.height === canvasHeight) return;
    const all = strokes.current;
    const lowest = all.reduce((max, stroke) => Math.max(max, ...stroke.map(([, y]) => y)), 0);
    const room = canvasHeight - LINE_WIDTH * 2;
    if (lowest > room) {
      const factor = room / lowest;
      strokes.current = all.map((stroke) => stroke.map(([x, y]) => [x * factor, y * factor] as [number, number]));
    }
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d')!;
    ctx.strokeStyle = INK;
    ctx.fillStyle = INK;
    ctx.lineWidth = LINE_WIDTH;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const stroke of strokes.current) {
      if (stroke.length === 1) {
        ctx.beginPath();
        ctx.arc(stroke[0][0], stroke[0][1], 2, 0, Math.PI * 2);
        ctx.fill();
        continue;
      }
      ctx.beginPath();
      ctx.moveTo(stroke[0][0], stroke[0][1]);
      for (const [x, y] of stroke.slice(1)) ctx.lineTo(x, y);
      ctx.stroke();
    }
    if (strokes.current.length > 0) {
      onChange({ mode: 'drawn', image: canvas.toDataURL('image/png').split(',')[1] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasHeight]);

  const publish = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    onChange({ mode: 'drawn', image: canvas.toDataURL('image/png').split(',')[1] });
  }, [onChange]);

  const start = (event: React.PointerEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    canvasRef.current!.setPointerCapture(event.pointerId);
    drawing.current = true;
    last.current = point(event);
    strokes.current.push([[last.current.x, last.current.y]]);
    // Un simple point doit laisser une trace
    const ctx = canvasRef.current!.getContext('2d')!;
    ctx.fillStyle = INK;
    ctx.beginPath();
    ctx.arc(last.current.x, last.current.y, 2, 0, Math.PI * 2);
    ctx.fill();
  };

  const move = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || !last.current) return;
    event.preventDefault();
    const ctx = canvasRef.current!.getContext('2d')!;
    const next = point(event);
    ctx.strokeStyle = INK;
    ctx.lineWidth = LINE_WIDTH;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(next.x, next.y);
    ctx.stroke();
    strokes.current[strokes.current.length - 1]?.push([next.x, next.y]);
    last.current = next;
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    setHasInk(true);
    publish();
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext('2d')!.clearRect(0, 0, canvas.width, canvas.height);
    strokes.current = [];
    setHasInk(false);
    onChange(null);
  };

  // Le choix remonte au parent à chaque changement d'onglet
  useEffect(() => {
    if (mode === 'typed') {
      onChange({ mode: 'typed' });
    } else {
      onChange(hasInk && canvasRef.current ? { mode: 'drawn', image: canvasRef.current.toDataURL('image/png').split(',')[1] } : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  return (
    <div className="space-y-3">
      <h3 className="font-semibold text-gray-900">Votre signature</h3>

      {style === 'draw_or_type' && (
        <div className="flex gap-2" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'drawn'}
            onClick={() => setMode('drawn')}
            className={`btn flex-1 ${mode === 'drawn' ? 'btn-primary' : 'btn-secondary'}`}
          >
            ✍️ Tracer
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'typed'}
            onClick={() => setMode('typed')}
            className={`btn flex-1 ${mode === 'typed' ? 'btn-primary' : 'btn-secondary'}`}
          >
            ⌨️ Taper mon nom
          </button>
        </div>
      )}

      {/* Le canvas reste monté (masqué en mode "taper", déplacé en plein écran) pour conserver le tracé */}
      {style !== 'type' && (
        <div
          className={`${mode === 'drawn' ? '' : 'hidden'} ${expanded ? 'fixed inset-0 z-50 flex flex-col bg-white p-3' : ''}`}
          {...(expanded ? { role: 'dialog', 'aria-modal': true, 'aria-label': 'Signature en plein écran' } : {})}
        >
          {expanded && (
            <div className="flex items-center justify-between gap-2 pb-3" style={{ minHeight: TOOLBAR - 12 }}>
              <h3 className="font-semibold text-gray-900">Votre signature</h3>
              <div className="flex items-center gap-2">
                <button type="button" onClick={clear} disabled={!hasInk} className="btn btn-secondary disabled:opacity-40">
                  Effacer
                </button>
                <button type="button" onClick={() => setExpanded(false)} className="btn btn-primary">
                  Terminé
                </button>
              </div>
            </div>
          )}
          <div className={expanded ? 'flex flex-1 items-center justify-center' : ''}>
            <canvas
              ref={canvasRef}
              width={CANVAS_WIDTH}
              height={CANVAS_HEIGHT}
              onPointerDown={start}
              onPointerMove={move}
              onPointerUp={end}
              onPointerCancel={end}
              onPointerLeave={end}
              aria-label="Zone pour tracer votre signature"
              className={`bg-white border-2 border-dashed border-gray-400 rounded-lg ${expanded ? '' : 'w-full'}`}
              style={{
                touchAction: 'none',
                cursor: 'crosshair',
                aspectRatio: `${CANVAS_WIDTH} / ${canvasHeight}`,
                ...(expanded ? { width: expandedWidth } : {}),
              }}
            />
          </div>
          {expanded && portrait && (
            <p className="pt-3 text-center text-sm text-gray-600">
              <span aria-hidden="true">↻ </span>Tournez votre téléphone à l'horizontale : la zone s'agrandit encore.
            </p>
          )}
          {!expanded && (
            <div className="flex justify-between items-center mt-2 gap-3">
              <p className="text-xs text-gray-500">Tracez votre signature avec le doigt, le stylet ou la souris.</p>
              <div className="flex items-center gap-3 shrink-0">
                <button type="button" onClick={() => setExpanded(true)} className="text-sm font-medium text-primary-600 underline">
                  Agrandir
                </button>
                <button type="button" onClick={clear} disabled={!hasInk} className="text-sm text-primary-600 underline disabled:opacity-40">
                  Effacer
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {mode === 'typed' && (
        <div className="border-2 border-dashed border-gray-400 rounded-lg p-6 bg-white text-center">
          <p className="text-4xl text-gray-900" style={{ fontFamily: '"Brush Script MT", "Segoe Script", cursive' }}>
            {signerName}
          </p>
          <p className="text-xs text-gray-500 mt-3">
            Votre nom sera apposé en écriture manuscrite sur le document (aperçu : le rendu final peut différer légèrement).
          </p>
        </div>
      )}
    </div>
  );
}

export default SignaturePad;
